// @flow
type User = {
  user_id?: number,
  name?: string,
  email?: string,
  created_at?: number,
  avatar?: {
    type?: 'avatar',
    image_url?: string,
  },
  company?: {
    id?: string,
    name?: string,
    plan?: string,
  },
  ...CustomAttributes,
}

type CustomAttributes = {
  // Reserved for future use.
}

export type IntercomSettings = {
  app_id: string,
  horizontal_padding?: number,
  vertical_padding?: number,
  hide_default_launcher?: boolean,
  ...User,
}

declare var window: {
  intercomSettings: IntercomSettings,
  Intercom: {
    (...mixed[]): void,
    unattached?: boolean,
    ...
  },
}

type Method =
  | 'boot'
  | 'getVisitorId'
  | 'hide'
  | 'onHide'
  | 'onShow'
  | 'onUnreadCountChange'
  | 'reattach_activator'
  | 'show'
  | 'showMessage'
  | 'showNewMessage'
  | 'shutdown'
  | 'trackEvent'
  | 'update'

export function initialize(settings: IntercomSettings) {
  window.intercomSettings = settings

  const Intercom = function () {
    Intercom.c(arguments)
  }
  Intercom.unattached = true
  Intercom.q = []
  Intercom.c = function (args) {
    Intercom.q.push(args)
  }

  window.Intercom = Intercom
}

export function boot() {
  return IntercomApi('boot', window.intercomSettings)
}

export function shutdown() {
  return IntercomApi('shutdown')
}

export function changeUser(userSettings: User & CustomAttributes) {
  update(userSettings)
}

export function showNewMessage(message?: string) {
  IntercomApi('showNewMessage', message)
}

export function hide() {
  IntercomApi('hide')
}

export function update(settings?: $Shape<IntercomSettings>) {
  if (settings) {
    window.intercomSettings = {
      ...window.intercomSettings,
      ...settings,
    }

    IntercomApi('update', settings)
  } else {
    IntercomApi('update')
  }
}

export function load() {
  if (window.Intercom.unattached) {
    window.Intercom.unattached = false
    const script = document.createElement('script')
    script.type = 'text/javascript'
    script.async = true
    script.src = `//widget.intercom.io/widget/${window.intercomSettings.app_id}`

    document.body && document.body.appendChild(script)
  } else {
    IntercomApi('reattach_activator')
    IntercomApi('update', window.intercomSettings)
  }
}

// Low-level API access.
function IntercomApi(method: Method, ...args: mixed[]) {
  return window.Intercom.apply(window.Intercom, [method, ...args])
}
